import { useContext, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic "tab-filtered gallery + detail drawer" over a *static* dataset.
 *
 * Reproduces the common pattern of a maquette where a secondary menu (tabs)
 * filters a grid of cards client-side, and clicking a card slides in a detail
 * panel. It owns the two pieces of local UI state that reactive-json cannot
 * express declaratively — the active filter and the open item — while keeping
 * every pixel of markup in YAML : each card and the drawer body are rendered
 * from *named templates* (the block's `templates:`) via `<View>`, pointed at
 * the item's real store path so `~.field` resolves exactly like inside a
 * `Switch`. No backend, no refetch : the whole collection lives in the block
 * `data:`.
 *
 * YAML usage:
 *   - type: FilteredGallery
 *     items: ~~.membres            # store array of objects
 *     filterField: discipline       # which field the tabs filter on
 *     filters: ~~.disciplines       # array of tab values (strings)
 *     allValue: 'Tous'              # the tab value that shows everything
 *     cardTemplate: membreCard      # template name → rendered per item (~. = item)
 *     panelTemplate: membreFiche    # template name → rendered for the open item
 *     countLabel: '{n} membres affichés'
 *     # …class/style props below drive the layout (defaults are minimal)…
 *
 * The card template should NOT be a `<button>` (the wrapper already is the
 * click target) — use a `div`, which also lets you drop a
 * per-row inline editing affordances (cells + `BuilderRowToolbar`) inside it.
 */

export const FilteredGallery = ({ props, path, datafield, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const filterField = resolve(props.filterField)
  let filters = resolve(props.filters)
  if (!Array.isArray(filters)) filters = []
  const allValue = resolve(props.allValue) ?? filters[0]
  const cardTemplate = resolve(props.cardTemplate)
  const panelTemplate = resolve(props.panelTemplate)
  const countLabel = resolve(props.countLabel)

  // Resolve the collection AND its store path, so each item template can be
  // rendered at `<basePath>.<index>` (that's what makes `~.field` resolve).
  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.items,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const items = Array.isArray(formData) ? formData : []

  const [active, setActive] = useState(allValue)
  const [selected, setSelected] = useState(null) // original index, or null

  // Close the drawer on Escape.
  useEffect(() => {
    if (selected == null) return
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [selected])

  // Keep original indices so the per-item View points at the real store slot.
  const visible = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => active === allValue || item?.[filterField] === active)

  const count = visible.length
  const countText = typeof countLabel === 'string' ? countLabel.replace('{n}', String(count)) : null

  const cls = (v, fallback) => (typeof v === 'string' ? v : fallback)

  const onTab = (value) => {
    setActive(value)
    setSelected(null)
  }

  return (
    <ActionDependant {...props}>
      <div className={cls(props.rootClass, '')}>

        {/* ── Sous-menu : barre de filtres ── */}
        <div className={cls(props.tabsBarClass, 'border-b border-black/10')}>
          <div className={cls(props.tabsContainerClass, 'max-w-[1280px] mx-auto px-5')}>
            <div className={cls(props.tabsRowClass, 'flex items-center overflow-x-auto')}>
              {filters.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onTab(value)}
                  className={`${cls(props.tabBaseClass, 'flex-shrink-0 transition-opacity hover:opacity-70')} ${
                    active === value
                      ? cls(props.tabActiveClass, 'font-semibold')
                      : cls(props.tabInactiveClass, 'opacity-70')
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Contenu : compteur + grille ── */}
        <div className={cls(props.contentClass, 'max-w-[1280px] mx-auto px-6 pt-14 pb-20')}>
          {countText != null && (
            <p className={cls(props.countClass, 'mb-8 text-sm')}>{countText}</p>
          )}
          <div className={cls(props.gridClass, 'grid grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4 @7xl:grid-cols-6 gap-6')}>
            {visible.map(({ index }) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(index)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) setSelected(index) }}
                className={cls(props.cardClass, 'group flex flex-col items-center text-center w-full cursor-pointer hover:opacity-85 transition-opacity relative')}
              >
                <View props={{ load: cardTemplate }} path={`${formDataPath}.${index}`} datafield={index} currentData={items[index]} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Panneau fiche (drawer) ── */}
        {selected != null && (
          <div
            className={cls(props.overlayClass, 'fixed inset-0 z-50 flex items-start justify-end')}
            style={props.overlayStyle ? resolve(props.overlayStyle) : { background: 'rgba(0,0,0,0.6)' }}
            onClick={() => setSelected(null)}
          >
            <div
              className={cls(props.drawerClass, 'h-full overflow-y-auto bg-white shadow-2xl relative')}
              style={props.drawerStyle ? resolve(props.drawerStyle) : { width: 'min(500px, 100vw)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Fermer"
                className={cls(props.closeButtonClass, 'absolute top-5 right-5 z-10 opacity-60 hover:opacity-100 transition-opacity')}
              >
                <X size={20} strokeWidth={1.5} />
              </button>
              <View props={{ load: panelTemplate }} path={`${formDataPath}.${selected}`} datafield={selected} currentData={items[selected]} />
            </div>
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
